import { useEffect, useState } from "react";
import { Icon } from "@/components/icons";
import {
  ANNOUNCEMENTS,
  activeAnnouncements,
  formatDueAt,
  formatDueStatus,
  isDismissalStale,
  msUntilNextAnnouncementChange,
  type Announcement,
  type AnnouncementTone,
} from "@/src/config/announcements";

const DISMISS_COOKIE_PREFIX = "announcement_dismissed_";

const TONES: Record<
  AnnouncementTone,
  { container: string; icon: string; iconChip: string; chip: string }
> = {
  info: {
    container: "border-grey-200 bg-grey-50",
    icon: "info",
    iconChip: "border-grey-200 bg-white text-grey-600",
    chip: "border-grey-300 bg-white text-grey-900",
  },
  warning: {
    container: "border-yellow-700 bg-yellow-100",
    icon: "warning",
    iconChip: "border-yellow-300 bg-white text-yellow-700",
    chip: "border-yellow-300 bg-white text-yellow-700",
  },
  critical: {
    container: "border-red-500 bg-red-100",
    icon: "error",
    iconChip: "border-red-400 bg-white text-red-600",
    chip: "border-red-400 bg-white text-red-600",
  },
};

function dismissCookie(id: string): string {
  return `${DISMISS_COOKIE_PREFIX}${id}`;
}

function readDismissedAt(id: string): Date | null {
  if (typeof document === "undefined") return null;
  const prefix = `${dismissCookie(id)}=`;
  const raw = document.cookie.split("; ").find((c) => c.startsWith(prefix));
  if (!raw) return null;
  const ms = Number(raw.slice(prefix.length));
  return Number.isFinite(ms) ? new Date(ms) : null;
}

function persistDismissed(announcement: Announcement, at: Date) {
  if (typeof document === "undefined") return;
  const endsAt = new Date(announcement.endsAt).getTime();
  const maxAge = Math.max(60, Math.ceil((endsAt - at.getTime()) / 1000));
  const cookie = `${dismissCookie(announcement.id)}=${at.getTime()}`;
  document.cookie = `${cookie}; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function useAnnouncementClock(announcements: readonly Announcement[]): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const delay = msUntilNextAnnouncementChange(now, announcements);
    if (delay == null) return;
    const timer = setTimeout(() => setNow(new Date()), Math.min(delay, 5 * 60_000));
    return () => clearTimeout(timer);
  }, [now, announcements]);

  return now;
}

export function AnnouncementBanner({
  announcement,
  now = new Date(),
  onDismiss,
  className = "",
}: {
  announcement: Announcement;
  now?: Date;
  onDismiss?: () => void;
  className?: string;
}) {
  const tone = TONES[announcement.tone ?? "info"];
  const due = announcement.dueAt ? formatDueAt(announcement.dueAt) : null;
  const status = announcement.dueAt ? formatDueStatus(announcement.dueAt, now) : null;
  const dismissible = announcement.dismissible ?? true;

  return (
    <section
      aria-label={`Announcement: ${announcement.title}`}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 ${tone.container} ${className}`}
    >
      <span
        aria-hidden
        className={`flex size-9 shrink-0 items-center justify-center rounded-sm border ${tone.iconChip}`}
      >
        <Icon name={announcement.icon ?? tone.icon} size={20} />
      </span>

      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-body font-semibold text-black">{announcement.title}</p>
          {status && (
            <span
              className={`shrink-0 rounded-sm border px-1.5 py-0.5 text-tiny font-medium ${tone.chip}`}
            >
              {status}
            </span>
          )}
        </div>

        <p className="text-small text-grey-900">{announcement.description}</p>

        {due && (
          <p className="text-small text-grey-600">
            <time dateTime={announcement.dueAt}>{due.utc}</time>
          </p>
        )}

        {announcement.link && (
          <a
            href={announcement.link.href}
            target="_blank"
            rel="noreferrer"
            className="mt-1 inline-flex w-fit items-center gap-1 text-small font-medium text-green-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
          >
            {announcement.link.label}
            <Icon name="open_in_new" size={20} />
          </a>
        )}
      </div>

      {dismissible && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={`Dismiss announcement: ${announcement.title}`}
          className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-grey-300 bg-white text-grey-600 transition-colors hover:text-black focus:outline-none focus-visible:ring-2 focus-visible:ring-grey-500-32"
        >
          <Icon name="close" size={20} />
        </button>
      )}
    </section>
  );
}

export function AnnouncementBanners({
  announcements = ANNOUNCEMENTS,
  className = "",
}: {
  announcements?: readonly Announcement[];
  className?: string;
}) {
  const now = useAnnouncementClock(announcements);
  const active = activeAnnouncements(now, announcements);
  const [dismissedAt, setDismissedAt] = useState<Record<string, number>>({});

  const visible = active.filter((a) => {
    if (a.dismissible === false) return true;
    const at = dismissedAt[a.id] ?? readDismissedAt(a.id)?.getTime();
    return at == null || isDismissalStale(new Date(at), now);
  });
  if (visible.length === 0) return null;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {visible.map((announcement) => (
        <AnnouncementBanner
          key={announcement.id}
          announcement={announcement}
          now={now}
          onDismiss={() => {
            const at = new Date();
            persistDismissed(announcement, at);
            setDismissedAt((prev) => ({ ...prev, [announcement.id]: at.getTime() }));
          }}
        />
      ))}
    </div>
  );
}
